import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Pipette, AlertTriangle } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const QueueSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	color: Yup.string()
		.min(3, "Cor inválida")
		.max(9, "Cor inválida")
		.required("Escolha uma cor"),
	greetingMessage: Yup.string(),
});

// O backend exige cor única por fila. Uma cor fixa como padrão fazia o
// cadastro falhar já na segunda fila: o usuário abria, preenchia o nome,
// salvava e não acontecia nada — a cor sugerida já estava em uso.
const PALETA = [
	"#38bdf8",
	"#34d399",
	"#f472b6",
	"#fbbf24",
	"#a78bfa",
	"#fb7185",
	"#4ade80",
	"#22d3ee",
	"#f97316",
	"#818cf8",
];

const corLivre = (usadas) => {
	const disponivel = PALETA.find(
		(c) => !usadas.includes(c.toLowerCase())
	);

	if (disponivel) return disponivel;

	// Paleta esgotada: sorteia até achar uma que ninguém usa.
	let tentativa;
	do {
		tentativa = `#${Math.floor(Math.random() * 0xffffff)
			.toString(16)
			.padStart(6, "0")}`;
	} while (usadas.includes(tentativa));

	return tentativa;
};

const QueueModal = ({ open, onClose, queueId }) => {
	const initialState = {
		name: "",
		color: "#38bdf8",
		greetingMessage: "",
		integrationId: "",
		departmentId: "",
	};

	const [integracoes, setIntegracoes] = useState([]);
	const [departments, setDepartments] = useState([]);

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [queue, setQueue] = useState(initialState);
	const greetingRef = useRef();

	// Ao abrir para criar, já sugere uma cor que ninguém está usando.
	useEffect(() => {
		if (!open || queueId) return;

		(async () => {
			try {
				const { data } = await api.get("/queue");
				const usadas = (Array.isArray(data) ? data : [])
					.map((q) => String(q.color || "").toLowerCase())
					.filter(Boolean);

				setQueue((prev) => ({ ...prev, color: corLivre(usadas) }));
			} catch (err) {
				// Sem a lista fica a cor padrão; o erro do backend ainda avisa.
			}
		})();
	}, [open, queueId]);

	// Lista as integrações para o seletor. Sem nenhuma cadastrada o campo nem
	// aparece — não adianta oferecer escolha vazia.
	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/queue-integrations");
				setIntegracoes((data || []).filter((i) => i.isActive));
			} catch (err) {
				// Falhar aqui não pode impedir de criar ou editar a fila.
			}
			try {
				const { data: deptData } = await api.get("/departments");
				setDepartments(deptData.departments || deptData || []);
			} catch (err) {
				// Falhar aqui não pode impedir de criar ou editar a fila.
			}
		})();
	}, [open]);

	useEffect(() => {
		(async () => {
			if (!queueId) return;
			try {
				const { data } = await api.get(`/queue/${queueId}`);
				setQueue((prevState) => {
					// O Select do Radix não aceita null como valor: sem fila ligada a
					// nenhuma integração, o campo tem de ser string vazia.
					return {
						...prevState,
						...data,
						integrationId: data.integrationId ? String(data.integrationId) : "",
						departmentId: data.departmentId ? String(data.departmentId) : "",
					};
				});
			} catch (err) {
				toastError(err);
			}
		})();

		// Volta ao initialState (e não a um objeto vazio) para não perder
		// a cor padrão ao reabrir o modal.
		return () => {
			setQueue(initialState);
		};
	}, [queueId, open]);

	const handleClose = () => {
		onClose();
		setQueue(initialState);
	};

	const handleSaveQueue = async (values) => {
		try {
			const corpo = {
				...values,
				integrationId: values.integrationId ? +values.integrationId : null,
				departmentId: values.departmentId ? +values.departmentId : null,
			};

			if (queueId) {
				await api.put(`/queue/${queueId}`, corpo);
			} else {
				await api.post("/queue", corpo);
			}
			toast.success(queueId ? "Fila atualizada." : "Fila criada.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{queueId ? "Editar fila" : "Nova fila"}
					</DialogTitle>
					<DialogDescription>
						Fila é um assunto do atendimento — Suporte, Financeiro, Vendas.
						Quando a conexão tem mais de uma, o cliente escolhe pelo número e
						a conversa cai com quem atende aquele assunto.
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={queue}
					enableReinitialize={true}
					validationSchema={QueueSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveQueue(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="name">{i18n.t("queueModal.form.name")}</Label>
									<Field as={Input} id="name" name="name" autoFocus placeholder="Ex.: Suporte" />
									{touched.name && errors.name && (
										<p className="text-xs text-destructive">{errors.name}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										O cliente lê este nome no menu de escolha.
									</p>
								</div>
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="color">{i18n.t("queueModal.form.color")}</Label>
									<div className="relative">
										<span
											className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-sm border"
											style={{ backgroundColor: values.color }}
										/>
										<Field
											as={Input}
											id="color"
											name="color"
											className="pl-9 pr-9"
											onFocus={() => {
												setColorPickerModalOpen(true);
												greetingRef.current?.focus();
											}}
										/>
										<button
											type="button"
											onClick={() => setColorPickerModalOpen(true)}
											className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
										>
											<Pipette className="h-4 w-4" />
										</button>
									</div>
									{touched.color && errors.color && (
										<p className="text-xs text-destructive">{errors.color}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										Marca as conversas desta fila na lista. Duas filas não podem
										ter a mesma cor.
									</p>
								</div>
							</div>

							<ColorPicker
								open={colorPickerModalOpen}
								currentColor={values.color}
								handleClose={() => setColorPickerModalOpen(false)}
								onChange={(color) => {
									setFieldValue("color", color);
									setQueue((prev) => ({ ...prev, ...values, color }));
								}}
							/>

							{departments.length > 0 && (
								<div className="space-y-1.5">
									<Label>Departamento</Label>
									<Select
										value={values.departmentId || "nenhum"}
										onValueChange={(v) =>
											setFieldValue("departmentId", v === "nenhum" ? "" : v)
										}
									>
										<SelectTrigger>
											<SelectValue placeholder="Sem departamento" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="nenhum">Sem departamento</SelectItem>
											{departments.map((d) => (
												<SelectItem key={d.id} value={String(d.id)}>
													{d.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Todas as conversas que entrarem por esta fila ficarão
										marcadas com este departamento automaticamente.
									</p>
								</div>
							)}

							<div className="space-y-1.5">
								<Label htmlFor="greetingMessage">
									{i18n.t("queueModal.form.greetingMessage")}
								</Label>
								<Field
									as={Textarea}
									id="greetingMessage"
									name="greetingMessage"
									innerRef={greetingRef}
									rows={5}
									placeholder="Ex.: Você está no Suporte. Já vamos te atender!"
								/>
								{touched.greetingMessage && errors.greetingMessage && (
									<p className="text-xs text-destructive">{errors.greetingMessage}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Enviada no ato, assim que o cliente escolhe esta fila. Em
									branco, não manda nada.
								</p>
								{/* Silêncio depois de escolher a opção é a queixa mais comum
								    de quem usa: o cliente acha que não foi. */}
								{!values.greetingMessage && (
									<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
										<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										Sem saudação, quem escolher esta fila fica sem resposta até
										alguém da equipe abrir a conversa — e costuma achar que a
										mensagem não chegou.
									</p>
								)}
							</div>

							{integracoes.length > 0 && (
								<div className="space-y-1.5">
									<Label htmlFor="integrationId">Quem atende esta fila</Label>
									<Select
										value={values.integrationId || "nenhuma"}
										onValueChange={(v) =>
											setFieldValue("integrationId", v === "nenhuma" ? "" : v)
										}
									>
										<SelectTrigger id="integrationId">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="nenhuma">
												A equipe (com o menu, se houver)
											</SelectItem>
											{integracoes.map((integracao) => (
												<SelectItem
													key={integracao.id}
													value={String(integracao.id)}
												>
													{integracao.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Escolhendo uma integração, é o bot de fora que responde
										quem cair nesta fila — a Inteligência Artificial não fala.
									</p>
								</div>
							)}

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("queueModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{queueId ? "Salvar alterações" : "Criar fila"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default QueueModal;
